import React, { useState, useEffect } from 'react';
import { BarChart3, DollarSign, Users, Package, Truck, Building2, TrendingUp, CreditCard, Wallet, ArrowRight, Handshake } from 'lucide-react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { adminAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';

const COLORS = ['#b8ff00', '#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981'];

function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-zinc-200 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}><Icon size={16} /></div>
        <span className="text-xs text-zinc-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="font-display font-bold text-2xl text-zinc-900" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
      {sub && <p className="text-xs text-zinc-400 mt-1">{sub}</p>}
    </motion.div>
  );
}

export default function SalesDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getDashboard().then(res => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#b8ff00] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><p className="text-zinc-500">Dashboard unavailable</p></div>;

  const paymentData = [
    { name: 'Card', value: data.payments.card, fill: '#8b5cf6' },
    { name: 'Wallet', value: data.payments.wallet, fill: '#b8ff00' },
  ].filter(d => d.value > 0);

  const orderStatusData = [
    { name: 'Paid', value: data.orders.paid, fill: '#10b981' },
    { name: 'Pending', value: data.orders.pending, fill: '#f59e0b' },
    { name: 'Cancelled', value: data.orders.cancelled, fill: '#ef4444' },
  ].filter(d => d.value > 0);

  return (
    <div className="min-h-screen bg-zinc-50 pb-8">
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-zinc-900 to-zinc-700 rounded-2xl flex items-center justify-center">
              <BarChart3 className="text-[#b8ff00]" size={28} />
            </div>
            <div>
              <h1 className="font-display font-bold text-2xl text-zinc-900" data-testid="dashboard-title">Sales Dashboard</h1>
              <p className="text-zinc-500 text-sm">Platform-wide metrics & fund flow</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Top Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={DollarSign} label="Total GMV" value={formatCurrency(data.revenue.total_gmv)} color="bg-green-100 text-green-600" />
          <StatCard icon={Building2} label="Platform Revenue" value={formatCurrency(data.revenue.platform_revenue)} color="bg-amber-100 text-amber-600" sub="5% of delivery fees" />
          <StatCard icon={Package} label="Total Orders" value={data.orders.total} color="bg-blue-100 text-blue-600" sub={`${data.orders.paid} paid`} />
          <StatCard icon={Users} label="Users" value={data.users.total} color="bg-violet-100 text-violet-600" sub={`${data.users.vendors} vendors, ${data.users.buyers} buyers`} />
        </div>

        {/* Fund Flow Overview */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6" data-testid="fund-flow-overview">
          <h2 className="font-display font-semibold text-lg text-zinc-900 mb-4 flex items-center gap-2">
            <ArrowRight size={18} className="text-zinc-500" /> Money Flow
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { label: 'Buyers Paid', value: data.revenue.total_gmv, icon: CreditCard, color: 'bg-blue-50 border-blue-200 text-blue-700', arrow: true },
              { label: 'To Vendors', value: data.revenue.vendor_payouts, icon: DollarSign, color: 'bg-green-50 border-green-200 text-green-700', arrow: true },
              { label: 'To Drivers (95%)', value: data.revenue.driver_payouts, icon: Truck, color: 'bg-cyan-50 border-cyan-200 text-cyan-700', arrow: true },
              { label: 'MMarketing (5%)', value: data.revenue.platform_revenue, icon: Building2, color: 'bg-amber-50 border-amber-200 text-amber-700', arrow: true },
              { label: 'Withdrawn', value: data.revenue.total_withdrawn, icon: Wallet, color: 'bg-red-50 border-red-200 text-red-700', arrow: false },
            ].map(({ label, value, icon: Icon, color, arrow }, i) => (
              <div key={label} className="flex items-center gap-2">
                <div className={`flex-1 p-3 rounded-xl border ${color}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={14} /><span className="text-xs font-medium">{label}</span>
                  </div>
                  <p className="font-mono font-bold text-lg">{formatCurrency(value)}</p>
                </div>
                {arrow && i < 4 && <ArrowRight size={14} className="text-zinc-300 hidden md:block flex-shrink-0" />}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Order Status Pie */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <h3 className="font-display font-semibold mb-4">Order Status</h3>
            {orderStatusData.length > 0 ? (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="50%" height={180}>
                  <PieChart>
                    <Pie data={orderStatusData} dataKey="value" cx="50%" cy="50%" outerRadius={70} strokeWidth={2}>
                      {orderStatusData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip formatter={(v) => v} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {orderStatusData.map((d, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.fill }} />
                      <span className="text-sm text-zinc-600">{d.name}</span>
                      <span className="text-sm font-mono font-bold text-zinc-900 ml-auto">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-zinc-400 text-sm py-8 text-center">No order data</p>
            )}
          </div>

          {/* Payment Methods Pie */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <h3 className="font-display font-semibold mb-4">Payment Methods</h3>
            {paymentData.length > 0 ? (
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="50%" height={180}>
                  <PieChart>
                    <Pie data={paymentData} dataKey="value" cx="50%" cy="50%" outerRadius={70} strokeWidth={2}>
                      {paymentData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip formatter={(v) => v} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {paymentData.map((d, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.fill }} />
                      <span className="text-sm text-zinc-600">{d.name}</span>
                      <span className="text-sm font-mono font-bold text-zinc-900 ml-auto">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-zinc-400 text-sm py-8 text-center">No payment data</p>
            )}
          </div>
        </div>

        {/* Top Products */}
        {data.top_products.length > 0 && (
          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <h3 className="font-display font-semibold mb-4">Top Products by Revenue</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.top_products}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="revenue" fill="#b8ff00" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Deal Rooms & Recent Orders */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Handshake size={18} /> Deal Rooms</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-zinc-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-zinc-900">{data.deal_rooms.total}</p>
                <p className="text-xs text-zinc-500">Total</p>
              </div>
              <div className="bg-green-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{data.deal_rooms.active}</p>
                <p className="text-xs text-zinc-500">Active</p>
              </div>
              <div className="bg-[#b8ff00]/10 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-zinc-900">{data.deal_rooms.deals_made}</p>
                <p className="text-xs text-zinc-500">Deals Made</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <h3 className="font-display font-semibold mb-4">Recent Withdrawals</h3>
            {data.withdrawals.length > 0 ? (
              <div className="space-y-2">
                {data.withdrawals.slice(0, 5).map((w, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-zinc-50 rounded-lg">
                    <span className="text-sm text-zinc-600">#{w.id?.slice(0, 8)}</span>
                    <span className="font-mono font-bold text-sm">{formatCurrency(w.amount)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${w.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{w.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-400 text-sm text-center py-6">No withdrawals yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
